// The queue judge's ANSWER and the pure validator that decides whether the answer may
// be applied (#7105). The judge (`queue.judge`, `apps/agents-manager`) reads one repo's
// pending board and answers once per task; nothing it says reaches a row until
// `validateQueueJudgement` accepts the WHOLE answer. Policy stays in the prompt — this
// file holds only the refusals no prompt may talk its way past.

import { z } from 'zod';
import type { RawTaskSourceClass } from '../entities/raw-task/source-class';

const QueueTaskRefSchema = z.string().min(1);

/**
 * The merge block's markers — the ONE spelling. A `merge_into` appends its amendment to
 * the target's ask between these (`packages/db` `queries/tasks/queue-merge-block.ts`), and
 * a revive strips exactly that span. The amendment is MODEL-written, so it may never carry
 * either prefix (`amendment_forges_marker`): a forged marker would let a revive strip, or
 * keep, text the merge never wrote.
 */
export const QUEUE_MERGE_MARKER_OPEN_PREFIX = '<!-- dz:queue-merged';
export const QUEUE_MERGE_MARKER_CLOSE_PREFIX = '<!-- /dz:queue-merged';
export const QUEUE_MERGE_MARKER_CLOSE = `${QUEUE_MERGE_MARKER_CLOSE_PREFIX} -->`;
/** The opening marker of the block a merge of `sourceId` writes. */
export const queueMergeMarkerOpen = (sourceId: string): string =>
  `${QUEUE_MERGE_MARKER_OPEN_PREFIX} from ${sourceId} -->`;

/** Why a task is dropped. `low_value` is the one a signal-class task never takes. */
export const QueueDropKindSchema = z.enum(['premise_gone', 'obsolete', 'low_value', 'duplicate']);
export type QueueDropKind = z.infer<typeof QueueDropKindSchema>;

/**
 * What a drop cites. Each present field must RESOLVE: `merged_pr` to a member of the
 * board's `mergedPrs`, `task_id` to another task on the board. Both optional in shape,
 * at least one required by {@link validateQueueJudgement} — a drop citing nothing is a
 * claim with no fact behind it.
 */
export const QueueDropEvidenceSchema = z.object({
  merged_pr: z.number().int().positive().optional(),
  task_id: QueueTaskRefSchema.optional(),
});
export type QueueDropEvidence = z.infer<typeof QueueDropEvidenceSchema>;

/** One verdict on one task — `keep` ranks it, the other three change its row. */
export const QueueJudgementEntrySchema = z.discriminatedUnion('action', [
  z.object({
    task_id: QueueTaskRefSchema,
    action: z.literal('keep'),
    /** Ordinal within the board's tasks, 1 first, at most their count (`rank_out_of_range`). */
    rank: z.number().int().positive(),
  }),
  z.object({
    task_id: QueueTaskRefSchema,
    action: z.literal('merge_into'),
    into_task_id: QueueTaskRefSchema,
    /** Appended to the target's ask: what this task adds that the target lacks. */
    amendment: z.string().min(1),
  }),
  z.object({
    task_id: QueueTaskRefSchema,
    action: z.literal('drop'),
    kind: QueueDropKindSchema,
    evidence: QueueDropEvidenceSchema,
  }),
  z.object({
    task_id: QueueTaskRefSchema,
    action: z.literal('revive'),
  }),
]);
export type QueueJudgementEntry = z.infer<typeof QueueJudgementEntrySchema>;

/** The judge's whole answer: one entry per board task, plus any revives. */
export const QueueJudgementSchema = z.object({
  judgements: z.array(QueueJudgementEntrySchema),
});
export type QueueJudgement = z.infer<typeof QueueJudgementSchema>;

/**
 * The facts the judge was shown, and the validator checks against.
 *
 * - `tasks` — the repo's pending tasks. `sourceClass` is the class AFTER the dupe-of
 *   inheritance (a task a human ask was folded into is `human`); `dependents` lists only
 *   LIVE tasks waiting on this one, on the board or not; `dependsOn` its own waits.
 * - `revivable` — tasks the controller itself recently cancelled, the only ids `revive`
 *   may name.
 * - `mergedPrs` — PR numbers recently merged on the repo, the only ones evidence may cite.
 */
export interface QueueBoard {
  readonly tasks: readonly {
    readonly id: string;
    readonly sourceClass: RawTaskSourceClass;
    /** Saved work: rankable, never retired or amended by a merge. */
    readonly wip: boolean;
    readonly dependents: readonly string[];
    readonly dependsOn: readonly string[];
  }[];
  readonly revivable: readonly string[];
  readonly mergedPrs: readonly number[];
}

/** Why an answer was refused — one code per rule, so a caller can count each. */
export const QueueJudgementRefusalCode = {
  /** A board task the answer never judged: the judge must account for every one. */
  OmittedTask: 'omitted_task',
  /** A keep / merge / drop of an id that is not on the board. */
  UnknownTask: 'unknown_task',
  /**
   * A keep ranked past the board's task count: a rank is an ordinal of THIS board, 1..N
   * (`tasks.queue_rank`), and the bound also keeps the value inside the int4 column.
   */
  RankOutOfRange: 'rank_out_of_range',
  /** A task judged twice: which verdict wins would be the applier's guess. */
  DuplicateEntry: 'duplicate_entry',
  /** A drop or merge of a human-class task: a person's ask is ranked, never removed. */
  HumanTaskProtected: 'human_task_protected',
  /** A drop or merge of a task carrying saved work. */
  WipTaskProtected: 'wip_task_protected',
  /** A merge amendment into a task carrying saved work. */
  MergeTargetWip: 'merge_target_wip',
  /** `low_value` on a signal-class task: a reported break is not judged unimportant. */
  LowValueOnSignal: 'low_value_on_signal',
  /** A merge into a task that is not on the board. */
  MergeTargetUnknown: 'merge_target_unknown',
  /** A merge into a task the same answer drops, or merges elsewhere. */
  MergeTargetRetired: 'merge_target_retired',
  /** A merge into itself, around a merge loop, or into a task that waits on it. */
  MergeCycle: 'merge_cycle',
  /** Drop evidence that cites nothing, itself, or a PR / task the board does not hold. */
  EvidenceUnresolved: 'evidence_unresolved',
  /** Drop evidence citing a task the same answer drops or merges: no survivor stands. */
  EvidenceRetired: 'evidence_retired',
  /** A merge amendment carrying a merge-block marker the db writes and strips by. */
  AmendmentForgesMarker: 'amendment_forges_marker',
  /** A drop or merge that leaves a live dependent waiting on a cancelled task. */
  OrphansDependent: 'orphans_dependent',
  /** A revive of an id the controller did not recently cancel. */
  NotRevivable: 'not_revivable',
} as const;
export type QueueJudgementRefusalCode =
  (typeof QueueJudgementRefusalCode)[keyof typeof QueueJudgementRefusalCode];

export interface QueueJudgementRefusal {
  readonly task_id: string;
  readonly code: QueueJudgementRefusalCode;
  readonly detail: string;
}

/** Module-private: no value outside this file can carry it, so no literal is a {@link ValidatedQueueJudgement}. */
const VALIDATED: unique symbol = Symbol('ValidatedQueueJudgement');

/**
 * An answer {@link validateQueueJudgement} accepted, with the board it was checked
 * against. Minted ONLY there: the brand is a module-private `unique symbol`, so the db
 * apply, which accepts only this type, cannot be handed an unvalidated answer.
 */
export interface ValidatedQueueJudgement {
  readonly [VALIDATED]: true;
  readonly entries: readonly QueueJudgementEntry[];
  readonly board: QueueBoard;
}

export type QueueJudgementVerdict =
  | { readonly ok: true; readonly judgement: ValidatedQueueJudgement }
  | { readonly ok: false; readonly refusals: readonly QueueJudgementRefusal[] };

type BoardTask = QueueBoard['tasks'][number];

/**
 * Freeze `value` and everything it reaches. The minted judgement is a CLONE frozen this
 * way, so it shares no reference with the caller's answer or board: an accepted drop
 * cannot be rewritten into a drop of a human task between validation and apply.
 */
function deepFreeze<T>(value: T): T {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) return value;
  for (const v of Object.values(value)) deepFreeze(v);
  return Object.freeze(value);
}
type Retiring = Extract<QueueJudgementEntry, { action: 'drop' | 'merge_into' }>;
type Merge = Extract<QueueJudgementEntry, { action: 'merge_into' }>;
type Drop = Extract<QueueJudgementEntry, { action: 'drop' }>;

interface Index {
  readonly tasks: ReadonlyMap<string, BoardTask>;
  /** The FIRST entry per id; a later duplicate is refused and otherwise ignored. */
  readonly entries: ReadonlyMap<string, QueueJudgementEntry>;
  readonly revivable: ReadonlySet<string>;
  readonly mergedPrs: ReadonlySet<number>;
}

type Refuse = (task_id: string, code: QueueJudgementRefusalCode, detail: string) => void;

const retires = (index: Index, id: string): boolean => {
  const action = index.entries.get(id)?.action;
  return action === 'drop' || action === 'merge_into';
};

/** Does `from` wait on `target`, directly or through other board tasks? */
function waitsOn(index: Index, from: string, target: string): boolean {
  const seen = new Set<string>();
  const stack = [...(index.tasks.get(from)?.dependsOn ?? [])];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (id === target) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    stack.push(...(index.tasks.get(id)?.dependsOn ?? []));
  }
  return false;
}

/** Does following `merge_into` from `start` come back to `start`? */
function mergeLoops(index: Index, start: string): boolean {
  const seen = new Set<string>();
  let at: QueueJudgementEntry | undefined = index.entries.get(start);
  while (at?.action === 'merge_into' && !seen.has(at.task_id)) {
    seen.add(at.task_id);
    if (at.into_task_id === start) return true;
    at = index.entries.get(at.into_task_id);
  }
  return false;
}

function checkMergeTarget(index: Index, e: Merge, refuse: Refuse): void {
  const into = e.into_task_id;
  if (into === e.task_id || mergeLoops(index, e.task_id)) {
    refuse(e.task_id, 'merge_cycle', `${e.task_id} merges back into itself through ${into}`);
    return;
  }
  if (!index.tasks.has(into)) {
    refuse(e.task_id, 'merge_target_unknown', `merge target ${into} is not on the board`);
    return;
  }
  if (index.tasks.get(into)?.wip) {
    refuse(e.task_id, 'merge_target_wip', `merge target ${into} carries saved work`);
  }
  if (retires(index, into)) {
    const action = index.entries.get(into)?.action;
    refuse(e.task_id, 'merge_target_retired', `merge target ${into} is itself ${action}`);
    return;
  }
  if (waitsOn(index, into, e.task_id)) {
    refuse(e.task_id, 'merge_cycle', `merge target ${into} waits on ${e.task_id}`);
  }
}

function checkEvidence(index: Index, e: Drop, refuse: Refuse): void {
  const { merged_pr, task_id } = e.evidence;
  const misses: string[] = [];
  if (merged_pr === undefined && task_id === undefined) misses.push('it cites nothing');
  if (merged_pr !== undefined && !index.mergedPrs.has(merged_pr)) {
    misses.push(`PR #${merged_pr} is not a recently merged PR`);
  }
  if (task_id === e.task_id) misses.push('it cites the dropped task itself');
  else if (task_id !== undefined && !index.tasks.has(task_id)) {
    misses.push(`task ${task_id} is not on the board`);
  }
  if (misses.length > 0) {
    refuse(
      e.task_id,
      'evidence_unresolved',
      `drop evidence does not resolve: ${misses.join('; ')}`,
    );
    return;
  }
  if (task_id !== undefined && retires(index, task_id)) {
    const action = index.entries.get(task_id)?.action;
    refuse(e.task_id, 'evidence_retired', `drop evidence ${task_id} is itself ${action}`);
  }
}

function checkAmendment(e: Merge, refuse: Refuse): void {
  const forged = [QUEUE_MERGE_MARKER_OPEN_PREFIX, QUEUE_MERGE_MARKER_CLOSE_PREFIX].filter((m) =>
    e.amendment.includes(m),
  );
  if (forged.length > 0) {
    refuse(
      e.task_id,
      'amendment_forges_marker',
      `amendment carries the merge marker ${forged.join(' and ')}; rewrite it without`,
    );
  }
}

function checkRetiring(index: Index, task: BoardTask, e: Retiring, refuse: Refuse): void {
  if (task.wip) {
    refuse(e.task_id, 'wip_task_protected', `${e.task_id} carries saved work: ${e.action} refused`);
  }
  if (task.sourceClass === 'human') {
    refuse(e.task_id, 'human_task_protected', `${e.task_id} is human-class: ${e.action} refused`);
  }
  const orphaned = task.dependents.filter((d) => !retires(index, d));
  if (orphaned.length > 0) {
    refuse(
      e.task_id,
      'orphans_dependent',
      `${e.action} of ${e.task_id} orphans live dependents ${orphaned.join(', ')}`,
    );
  }
  if (e.action === 'merge_into') {
    checkMergeTarget(index, e, refuse);
    checkAmendment(e, refuse);
    return;
  }
  if (e.kind === 'low_value' && task.sourceClass === 'signal') {
    refuse(e.task_id, 'low_value_on_signal', `${e.task_id} is signal-class: low_value refused`);
  }
  checkEvidence(index, e, refuse);
}

function checkEntry(index: Index, e: QueueJudgementEntry, refuse: Refuse): void {
  if (e.action === 'revive') {
    if (!index.revivable.has(e.task_id)) {
      refuse(e.task_id, 'not_revivable', `${e.task_id} was not recently cancelled by the queue`);
    }
    return;
  }
  const task = index.tasks.get(e.task_id);
  if (task === undefined) {
    refuse(e.task_id, 'unknown_task', `${e.task_id} is not a pending task on this board`);
    return;
  }
  if (e.action !== 'keep') {
    checkRetiring(index, task, e, refuse);
    return;
  }
  if (e.rank > index.tasks.size) {
    refuse(
      e.task_id,
      'rank_out_of_range',
      `keep rank ${e.rank} of ${e.task_id} is past the board's ${index.tasks.size} tasks (1..${index.tasks.size})`,
    );
  }
}

/**
 * Accept the judge's answer, or name every rule it breaks. PURE: the board is the
 * caller's read, and an accepted answer is still applied under the db's own CAS.
 * All-or-nothing on purpose — an answer with one illegal entry was produced by a judge
 * that misread the board, and its other entries are not trusted either.
 */
export function validateQueueJudgement(
  answer: QueueJudgement,
  board: QueueBoard,
): QueueJudgementVerdict {
  const refusals: QueueJudgementRefusal[] = [];
  const refuse: Refuse = (task_id, code, detail) => refusals.push({ task_id, code, detail });
  const entries = new Map<string, QueueJudgementEntry>();
  for (const e of answer.judgements) {
    if (entries.has(e.task_id)) {
      refuse(e.task_id, 'duplicate_entry', `${e.task_id} is judged more than once`);
    } else entries.set(e.task_id, e);
  }
  const index: Index = {
    tasks: new Map(board.tasks.map((t) => [t.id, t])),
    entries,
    revivable: new Set(board.revivable),
    mergedPrs: new Set(board.mergedPrs),
  };
  for (const t of board.tasks) {
    if (!entries.has(t.id)) refuse(t.id, 'omitted_task', `board task ${t.id} has no judgement`);
  }
  for (const e of entries.values()) checkEntry(index, e, refuse);
  if (refusals.length > 0) return { ok: false, refusals };
  // Cloned, then frozen deep: the judgement keeps exactly what was checked, whatever the
  // caller does to its own answer or board afterwards.
  const judgement: ValidatedQueueJudgement = deepFreeze({
    [VALIDATED]: true as const,
    entries: structuredClone(answer.judgements),
    board: structuredClone(board),
  });
  return { ok: true, judgement };
}
